<script lang="ts">
    import { push } from 'svelte-spa-router';
    import type { Event } from '../../api/events';
    import { generateCalendarUrls, resolveEventId } from '../../api/events';
    import * as t from '../../translations/Events.ts';
    
    export let events: Event[];
    
    // Sort events by date
    $: sortedEvents = [...events].sort((a, b) => 
        new Date(a.startDate).getTime() - new Date(b.startDate).getTime()
    );
    
    // Calendar dropdown state
    let showCalendarDropdown = false;
    let selectedEventForCalendar: Event | null = null;
    
    function getDateParts(event: Pick<Event, 'startDate' | 'isAllDayEvent'>) {
        const date = new Date(event.startDate);
        // All-day events reach us pinned to UTC midnight by normalizeEvent
        // (see api/events.ts). getDate()/getFullYear() have no timezone
        // option and always read local components, which rolls the day back
        // for viewers west of UTC -- read UTC components instead.
        const isAllDay = event.isAllDayEvent;
        return {
            month: date.toLocaleDateString('en-US', { month: 'short', ...(isAllDay ? { timeZone: 'UTC' } : {}) }).toUpperCase(),
            day: isAllDay ? date.getUTCDate() : date.getDate(),
            year: isAllDay ? date.getUTCFullYear() : date.getFullYear(),
        };
    }
    
    function handleEventClick(event: Event) {
        const id = resolveEventId(event);
        if (!id) return;
        push(`/Register/${id}`);
    }
    
    function toggleCalendarDropdown() {
        showCalendarDropdown = !showCalendarDropdown;
        if (!showCalendarDropdown) {
            selectedEventForCalendar = null;
        }
    }
    
    function selectEventForCalendar(event: Event) {
        selectedEventForCalendar = event;
    }
    
    function addToCalendar(service: 'google' | 'apple' | 'outlook' | 'yahoo') {
        if (!selectedEventForCalendar) return;
        
        const urls = generateCalendarUrls(selectedEventForCalendar);
        const url = urls[service];
        
        if (service === 'apple') {
            const link = document.createElement('a');
            link.href = url;
            link.download = `${selectedEventForCalendar.title}.ics`;
            link.click();
        } else {
            window.open(url, '_blank');
        }
        
        showCalendarDropdown = false;
        selectedEventForCalendar = null;
    }
    
    function downloadAllICS() {
        sortedEvents.forEach(event => {
            const urls = generateCalendarUrls(event);
            const link = document.createElement('a');
            link.href = urls.apple;
            link.download = `${event.title}.ics`;
            link.click();
        });
        showCalendarDropdown = false;
    }
</script>

<div class="mb-8">
    <!-- Header with Add to Calendar dropdown -->
    <div class="flex items-center justify-between mb-4">
        <h3 class="text-lg font-semibold text-gray-900">{t.Your_Schedule_Header()}</h3>
        
        <div class="relative">
            <button
                type="button"
                class="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[var(--vv-action-center-button-color,#0d9488)] focus:ring-offset-2"
                on:click={toggleCalendarDropdown}
            >
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                {t.Add_To_Calendar()}
                <svg class="w-4 h-4 transition-transform {showCalendarDropdown ? 'rotate-180' : ''}" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                </svg>
            </button>
            
            {#if showCalendarDropdown}
                <div class="absolute right-0 mt-2 w-64 bg-white border border-gray-200 rounded-lg shadow-lg z-10">
                    {#if !selectedEventForCalendar}
                        <div class="p-3 border-b border-gray-100">
                            <p class="text-xs font-medium text-gray-500 uppercase tracking-wide">{t.Select_Event()}</p>
                        </div>
                        <div class="max-h-48 overflow-y-auto">
                            {#each sortedEvents as event (event.id)}
                                <button
                                    type="button"
                                    class="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
                                    on:click={() => selectEventForCalendar(event)}
                                >
                                    <span class="flex-shrink-0 w-10 text-center">
                                        <span class="block text-xs text-gray-500">{getDateParts(event).month}</span>
                                        <span class="block text-sm font-semibold">{getDateParts(event).day}</span>
                                    </span>
                                    <span class="truncate">{event.title}</span>
                                </button>
                            {/each}
                        </div>
                        <div class="p-2 border-t border-gray-100">
                            <button
                                type="button"
                                class="w-full px-3 py-2 text-sm text-center text-gray-600 hover:bg-gray-50 rounded"
                                on:click={downloadAllICS}
                            >
                                {t.Download_All()}
                            </button>
                        </div>
                    {:else}
                        <div class="p-3 border-b border-gray-100">
                            <button
                                type="button"
                                class="text-xs text-[var(--vv-action-center-button-color,#0d9488)] hover:underline flex items-center gap-1"
                                on:click={() => selectedEventForCalendar = null}
                            >
                                <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
                                </svg>
                                Back
                            </button>
                            <p class="text-sm font-medium text-gray-900 mt-1 truncate">{selectedEventForCalendar.title}</p>
                        </div>
                        <div class="p-2">
                            <button type="button" class="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 rounded flex items-center gap-2" on:click={() => addToCalendar('google')}>
                                {t.Google_Calendar()}
                            </button>
                            <button type="button" class="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 rounded flex items-center gap-2" on:click={() => addToCalendar('apple')}>
                                {t.Apple_Calendar()}
                            </button>
                            <button type="button" class="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 rounded flex items-center gap-2" on:click={() => addToCalendar('outlook')}>
                                {t.Outlook_Calendar()}
                            </button>
                            <button type="button" class="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 rounded flex items-center gap-2" on:click={() => addToCalendar('yahoo')}>
                                {t.Yahoo_Calendar()}
                            </button>
                        </div>
                    {/if}
                </div>
            {/if}
        </div>
    </div>
    
    <!-- Horizontal Timeline with Date Cards -->
    <div class="overflow-x-auto py-2 -my-2 px-1">
        <div class="flex gap-4 min-w-min">
            {#each sortedEvents as event (event.id)}
                {@const dateParts = getDateParts(event)}
                
                <button 
                    type="button"
                    class="flex-shrink-0 flex flex-col items-center text-center cursor-pointer group focus:outline-none"
                    on:click={() => handleEventClick(event)}
                >
                    <!-- Date Card -->
                    <div class="w-20 rounded-lg border-2 border-[var(--vv-action-center-button-color,#0d9488)] overflow-hidden transition-all group-hover:shadow-md group-focus:ring-2 group-focus:ring-[var(--vv-action-center-button-color,#0d9488)] group-focus:ring-offset-2">
                        <!-- Month header -->
                        <div class="bg-[var(--vv-action-center-button-color,#0d9488)] text-white text-xs font-bold py-1 px-2">
                            {dateParts.month}
                        </div>
                        <!-- Day number -->
                        <div class="bg-white py-2 px-2">
                            <span class="text-2xl font-bold text-gray-900">{dateParts.day}</span>
                            <span class="block text-xs text-gray-500">{dateParts.year}</span>
                        </div>
                    </div>
                    
                    <!-- Event Title -->
                    <p class="mt-2 text-xs text-gray-700 max-w-[80px] line-clamp-2 text-center group-hover:text-[var(--vv-action-center-button-color,#0d9488)]">
                        {event.title}
                    </p>
                </button>
            {/each}
        </div>
    </div>
</div>
